import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Modal,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Editor } from "@monaco-editor/react";
import {
  CRAFTERHACK_DARK_THEME,
  CRAFTERHACK_LIGHT_THEME,
  defineCrafterhackThemes,
  getCrafterhackEditorOptions,
} from "../common/monacoAppTheme";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import CenterFocusWeakIcon from "@mui/icons-material/CenterFocusWeak";
import AddIcon from "@mui/icons-material/Add";
import {
  formatExecutionLimitsLabel,
  resolveLimitsForDisplay,
} from "../common/executionLimits";
import {
  isFunctionMode,
  getStarterCode,
  getSampleDisplay,
  buildAllRunTestCases,
  getEffectiveCaseData,
  functionModeInputPlaceholder,
  functionModeLanguagesForSignature,
} from "../common/functionMode";
import { defaultCode, languageMapping } from "../common/compilerLanguages";
import {
  executionRun,
  executionSubmit,
  shouldUseExecutionEngine,
} from "../api/executionApi";
import {
  useNavigate,
  useParams,
  Link as RouterLink,
  useSearchParams,
} from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useDispatch, useSelector } from "react-redux";
import { showAuth } from "../redux/authSlice";
import { getToken, formatProblemTitle } from "../common/utils";
import { saveLastActiveProblem } from "../common/practiceProgress";
import {
  formatProblemHtml,
  sanitizeProblemText,
} from "../common/sanitizeProblemHtml";
import { problemStatementSx } from "../common/problemStatementSx";
import proxy from "../api/axios";
import {
  loadProblem,
  patchProblemSolvedInCache,
  patchProblemAttemptedInCache,
} from "../api/queries";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Helmet } from "react-helmet";
import {
  ProblemNeighborNav,
  ProblemNeighborNavInline,
  ProblemEngagementBar,
} from "../components/problems/ProblemEngagement";
import ProblemSubmissionsTab from "../components/problems/ProblemSubmissionsTab";
import ProblemExplanationTab from "../components/problems/ProblemExplanationTab";
import ProblemDiscussionTab from "../components/problems/ProblemDiscussionTab";
import ProblemNotesTab from "../components/problems/ProblemNotesTab";
import ProblemSolveTimer from "../components/problems/ProblemSolveTimer";
import {
  PROBLEM_SHORTCUTS,
  areShortcutsEnabled,
  setShortcutsEnabled,
  matchProblemShortcut,
  altKey,
} from "../components/problems/problemShortcuts";
import { useProblemSubmissions } from "../hooks/useProblemSubmissions";
import ResizeHandle from "../components/ResizeHandle";
import IdeConsolePanel from "../components/IdeConsolePanel";
import ProblemShareButton from "../components/problems/ProblemShareButton";
import ProblemHint from "../components/AI/ProblemHint";

const formatString = (str) => {
  // Non-problem strings (submission status etc.) — newlines only
  return String(str ?? "")
    .replace(/\n/g, "<br />")
    .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;");
};

const stripHtml = (html = "") =>
  String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toPascalCase = (str = "") =>
  String(str)
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const FOCUS_MODE_KEY = "problem-focus-mode";
const GUEST_RUN_COUNT_KEY = "guest-run-count";
const GUEST_FREE_RUNS = 3;

const readGuestRunCount = () => {
  try {
    return Number(localStorage.getItem(GUEST_RUN_COUNT_KEY)) || 0;
  } catch {
    return 0;
  }
};

const readFocusMode = () => {
  try {
    const stored = localStorage.getItem(FOCUS_MODE_KEY);
    if (stored === null) return false;
    return stored === "true";
  } catch {
    return false;
  }
};

const ProblemBreadcrumb = ({ problemDetails, contestId }) => {
  if (!problemDetails || contestId) return null;

  const hasProgramPath = Boolean(
    problemDetails.level && problemDetails.section,
  );
  const topic = problemDetails.topics?.[0];
  const levelPath = `/practice-track?level=${encodeURIComponent(String(problemDetails.level))}`;
  const sectionPath = `${levelPath}&section=${encodeURIComponent(problemDetails.section)}`;

  const crumbSx = {
    fontSize: { xs: "12px", sm: "13px" },
    fontWeight: 550,
    letterSpacing: "-0.01em",
    maxWidth: { xs: 120, sm: 180 },
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <Breadcrumbs
      separator={
        <NavigateNextIcon fontSize="small" sx={{ color: "text.disabled" }} />
      }
      aria-label="problem breadcrumb"
      sx={{
        mb: 0,
        "& .MuiBreadcrumbs-ol": { flexWrap: "wrap" },
        "& .MuiBreadcrumbs-separator": { mx: 0.5 },
      }}
    >
      {hasProgramPath ? (
        <MuiLink
          component={RouterLink}
          to="/practice-track"
          underline="hover"
          color="inherit"
          sx={crumbSx}
        >
          Practice Track
        </MuiLink>
      ) : (
        <MuiLink
          component={RouterLink}
          to="/problems"
          underline="hover"
          color="inherit"
          sx={crumbSx}
        >
          Problems
        </MuiLink>
      )}

      {hasProgramPath && (
        <MuiLink
          component={RouterLink}
          to={levelPath}
          underline="hover"
          color="inherit"
          sx={crumbSx}
        >
          Level {problemDetails.level}
        </MuiLink>
      )}

      {hasProgramPath ? (
        <MuiLink
          component={RouterLink}
          to={sectionPath}
          underline="hover"
          color="inherit"
          sx={crumbSx}
        >
          {problemDetails.section}
        </MuiLink>
      ) : topic ? (
        <MuiLink
          component={RouterLink}
          to={`/problems?topic=${encodeURIComponent(topic)}`}
          underline="hover"
          color="inherit"
          sx={crumbSx}
        >
          {toPascalCase(topic)}
        </MuiLink>
      ) : null}

      <Typography
        color="text.primary"
        sx={{
          ...crumbSx,
          maxWidth: { xs: 160, sm: 240 },
          fontWeight: 700,
          letterSpacing: "-0.015em",
        }}
      >
        {formatProblemTitle(problemDetails.title)}
      </Typography>
    </Breadcrumbs>
  );
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const PANEL_SPLIT_MIN = 28;
const PANEL_SPLIT_MAX = 72;
const PANEL_SPLIT_DEFAULT = 56;
const EDITOR_PANEL_MIN_WIDTH = 280;
const SPLIT_HANDLE_WIDTH = 8;

const ProblemPage = ({
  problem,
  contestId = 0,
  contestJudgingMode = null,
  challenge = null,
  callBack = null,
  isAdmin = false,
}) => {
  const appTheme = useTheme();
  // Phones + iPads — always use compact focus layout (and Problem/Code panes)
  const isMobileLayout = useMediaQuery(appTheme.breakpoints.down("lg"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const storedLanguage = localStorage.getItem("lang");
  const storedFont = localStorage.getItem("fontSize");
  const loggedInUser = useSelector((state) => state.user.userInfo);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  useEffect(() => {
    if (isLoggedIn) localStorage.removeItem(GUEST_RUN_COUNT_KEY);
  }, [isLoggedIn]);
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const discussionPostId = searchParams.get("discussion");
  const [problemDetails, setProblemDetails] = useState(problem || null);
  const [code, setCode] = useState(defaultCode[storedLanguage || "java"]);
  const [language, setLanguage] = useState(storedLanguage || "java");
  const [fontSize, setFontSize] = useState(storedFont || 14);
  const [autoSuggest, setAutoSuggest] = useState(
    () => localStorage.getItem("autoSuggest") === "true",
  );
  const monacoOptions = useMemo(
    () => ({
      ...getCrafterhackEditorOptions(fontSize),
      selectOnLineNumbers: true,
      quickSuggestions: autoSuggest
        ? { other: true, comments: true, strings: true }
        : false,
      suggestOnTriggerCharacters: autoSuggest,
      wordBasedSuggestions: autoSuggest ? "currentDocument" : "off",
      parameterHints: { enabled: autoSuggest },
      tabCompletion: autoSuggest ? "on" : "off",
    }),
    [fontSize, autoSuggest],
  );
  const [solutions, setSolutions] = useState([]);
  const [activeSolutionId, setActiveSolutionId] = useState(null);
  const [deleteSolutionId, setDeleteSolutionId] = useState(null);
  const [theme, setTheme] = useState(
    appTheme.palette.mode === "dark"
      ? CRAFTERHACK_DARK_THEME
      : CRAFTERHACK_LIGHT_THEME,
  );
  useEffect(() => {
    setTheme(
      appTheme.palette.mode === "dark"
        ? CRAFTERHACK_DARK_THEME
        : CRAFTERHACK_LIGHT_THEME,
    );
  }, [appTheme.palette.mode]);
  const [openSettings, setOpenSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPanelMaximized, setIsPanelMaximized] = useState(false);
  const [shortcutsOn, setShortcutsOn] = useState(areShortcutsEnabled);
  const [customInput, setCustomInput] = useState("");
  const [selectedSampleIndex, setSelectedSampleIndex] = useState(0);
  const [useCustomInput, setUseCustomInput] = useState(false);
  const [selectedCustomIndex, setSelectedCustomIndex] = useState(0);
  const [functionCaseOverrides, setFunctionCaseOverrides] = useState({});
  const [customCases, setCustomCases] = useState([]);
  const [output, setOutput] = useState("");
  const [runResults, setRunResults] = useState(null);
  const [isCompiling, setIsCompiling] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [copiedText, setCopiedText] = useState(null); // To track the copied text
  const [timerAcceptedSignal, setTimerAcceptedSignal] = useState(0);

  const submissionTabIndex = contestId ? 1 : 2;
  const discussionTabIndex = contestId ? -1 : 3;
  const notesTabIndex = contestId || challenge ? -1 : 4;
  const [discussionCount, setDiscussionCount] = useState(0);

  const functionLanguages = useMemo(() => {
    if (!isFunctionMode(problemDetails)) {
      return [
        { id: "java", label: "Java" },
        { id: "python", label: "Python" },
        { id: "cpp", label: "C++" },
        { id: "javascript", label: "JS (Node)" },
        { id: "csharp", label: "C#" },
      ];
    }
    return functionModeLanguagesForSignature(problemDetails?.signature);
  }, [problemDetails]);

  useEffect(() => {
    if (!functionLanguages.some((lang) => lang.id === language)) {
      const fallback = functionLanguages[0]?.id || "java";
      setLanguage(fallback);
      localStorage.setItem("lang", fallback);
    }
  }, [functionLanguages, language]);

  useEffect(() => {
    if (discussionPostId && !contestId) {
      setCurrentTab(discussionTabIndex);
    }
  }, [discussionPostId, contestId, discussionTabIndex]);

  const {
    submissions,
    loading: submissionsLoading,
    error: submissionsError,
    refresh: refreshSubmissions,
  } = useProblemSubmissions({
    problemId: problemDetails?._id,
    contestId: contestId || null,
    isLoggedIn,
    activeTab: currentTab,
    submissionTabIndex,
  });

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 2000); // Reset copied state after 2s
    });
  };

  // Keyboard shortcuts (LeetCode-style)
  const handleRunCodeRef = useRef(null);
  const handleSubmitCodeRef = useRef(null);
  const toggleFullscreenRef = useRef(null);
  const closeTabRef = useRef(null);
  const toggleMaximizeRef = useRef(null);
  const workspaceRef = useRef(null);
  const splitContainerRef = useRef(null);
  const editorAreaRef = useRef(null);
  const [editorHeight, setEditorHeight] = useState(360);
  const [leftPanelPct, setLeftPanelPct] = useState(() => {
    const stored = Number(localStorage.getItem("problem-left-split"));
    return Number.isFinite(stored)
      ? clamp(stored, PANEL_SPLIT_MIN, PANEL_SPLIT_MAX)
      : PANEL_SPLIT_DEFAULT;
  });
  const [consoleHeight, setConsoleHeight] = useState(() => {
    const stored = Number(localStorage.getItem("problem-console-height"));
    return Number.isFinite(stored) ? clamp(stored, 100, 560) : 200;
  });
  const [consoleCollapsed, setConsoleCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem("problem-console-collapsed");
      if (stored !== null) return stored === "true";
      return readFocusMode();
    } catch {
      return false;
    }
  });
  const [focusMode, setFocusMode] = useState(readFocusMode);
  /** Mobile: show one pane at a time — stacked layout can't scroll under 100dvh lock. */
  const [mobilePane, setMobilePane] = useState("problem"); // 'problem' | 'code'

  const mobileConsoleMinHeight = () => {
    if (typeof window === "undefined") return 280;
    return Math.max(280, Math.min(380, Math.round(window.innerHeight * 0.38)));
  };

  const revealTestResults = () => {
    setConsoleCollapsed(false);
    if (isMobileLayout) {
      setMobilePane("code");
      setConsoleHeight((h) => Math.max(h, mobileConsoleMinHeight()));
      // After layout, bring the console into view
      requestAnimationFrame(() => {
        setTimeout(() => {
          document
            .querySelector('[data-tour="problem-console"]')
            ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 80);
      });
    }
  };
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!shortcutsOn) return;
      if (openSettings) return;

      const action = matchProblemShortcut(event);
      if (!action) return;

      const target = event.target;
      // Discussion composer owns ⌘↵ — don't steal it
      if (target?.closest?.("[data-discussion-composer]")) return;

      // Don't run/submit while viewing Discussion (unless focus is in the code editor)
      const inCodeEditor = Boolean(
        target?.closest?.(".monaco-editor") ||
        target?.closest?.('[data-tour="problem-code"]'),
      );
      if (
        !inCodeEditor &&
        currentTab === discussionTabIndex &&
        discussionTabIndex >= 0 &&
        (action === "run" || action === "submit")
      ) {
        return;
      }

      // Monaco uses a <textarea> for input — must NOT skip shortcuts there.
      // Only ignore plain form fields outside the editor (e.g. search).
      const tag = target?.tagName?.toLowerCase();
      if (
        !inCodeEditor &&
        (tag === "input" || tag === "select") &&
        action !== "run" &&
        action !== "submit"
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (action === "run" || action === "submit") {
        // On mobile, jump to Code pane so output/editor is visible
        setMobilePane((pane) => (pane === "problem" ? "code" : pane));
      }

      if (action === "run") handleRunCodeRef.current?.();
      else if (action === "submit") handleSubmitCodeRef.current?.();
      else if (action === "close-tab") closeTabRef.current?.();
      else if (action === "maximize") toggleMaximizeRef.current?.();
      else if (action === "fullscreen") toggleFullscreenRef.current?.();
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [shortcutsOn, openSettings, currentTab, discussionTabIndex]);

  const getStoredCode = (lang, problemDetails) => {
    return (
      localStorage.getItem(
        `${contestId}-${problemDetails ? problemDetails.slug : slug}-code-${lang}`,
      ) || getStarterCode(problemDetails, lang)
    );
  };

  const solutionsStorageKey = (lang, problemDetails) =>
    `${contestId}-${problemDetails ? problemDetails.slug : slug}-solutions-${lang}`;

  const activeSolutionStorageKey = (lang, problemDetails) =>
    `${contestId}-${problemDetails ? problemDetails.slug : slug}-active-solution-${lang}`;

  const persistSolutions = (list, lang, problemDetails) => {
    try {
      localStorage.setItem(
        solutionsStorageKey(lang, problemDetails),
        JSON.stringify(list),
      );
    } catch {
      /* ignore */
    }
  };

  const persistActiveSolutionId = (id, lang, problemDetails) => {
    try {
      localStorage.setItem(activeSolutionStorageKey(lang, problemDetails), id);
    } catch {
      /* ignore */
    }
  };

  // Loads (or migrates legacy single-buffer code into) the "Solutions" chip list for a language.
  const loadSolutions = (lang, problemDetails) => {
    let stored = null;
    try {
      stored = JSON.parse(
        localStorage.getItem(solutionsStorageKey(lang, problemDetails)) ||
          "null",
      );
    } catch {
      stored = null;
    }
    if (Array.isArray(stored) && stored.length) {
      const storedActiveId = localStorage.getItem(
        activeSolutionStorageKey(lang, problemDetails),
      );
      const active = stored.find((s) => s.id === storedActiveId) || stored[0];
      return { list: stored, activeId: active.id };
    }
    const list = [
      {
        id: "sol-1",
        label: "Solution 1",
        code: getStoredCode(lang, problemDetails),
      },
    ];
    return { list, activeId: list[0].id };
  };

  const applySolutions = (lang, problemDetails) => {
    const { list, activeId } = loadSolutions(lang, problemDetails);
    setSolutions(list);
    setActiveSolutionId(activeId);
    setCode((list.find((s) => s.id === activeId) || list[0]).code);
  };

  const loadContestProblem = (problem) => {
    if (!problem) return;
    setCurrentTab(0);
    setOutput("");
    setRunResults(null);
    setError(false);
    setUseCustomInput(false);
    setCustomCases([]);
    setSelectedSampleIndex(0);
    setSelectedCustomIndex(0);
    setFunctionCaseOverrides({});
    setProblemDetails(problem);
    applySolutions(language, problem);
  };

  useEffect(() => {
    loadContestProblem(problem);
  }, [problem]);

  useEffect(() => {
    setOutput("");
    setError(false);
    setCurrentTab(discussionPostId && !contestId ? discussionTabIndex : 0);
    if (problem) {
      loadContestProblem(problem);
      return undefined;
    }
    if (!slug) return undefined;

    // Drop stale problem immediately so prev/next aren't for the previous slug
    setProblemDetails((prev) => (prev?.slug === slug ? prev : null));

    let cancelled = false;
    const fetchProblemDetails = async () => {
      try {
        const data = await loadProblem(slug, isAdmin);
        if (cancelled) return;
        // Guard against out-of-order responses when clicking prev/next quickly
        if (data?.slug && data.slug !== slug) return;
        setProblemDetails(data);
        applySolutions(language, data);
        window.scrollTo(0, 0);
      } catch (err) {
        if (cancelled) return;
        toast.error("Problem does not exist");
        navigate("/");
        console.error("Error fetching problem details:", err);
      }
    };

    fetchProblemDetails();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, problem]);

  useEffect(() => {
    if (!problemDetails) return;
    // Don't overwrite editor from a stale problem while a new slug is loading
    if (slug && problemDetails.slug && problemDetails.slug !== slug) return;
    applySolutions(language, problemDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    if (!problemDetails?.slug || contestId || challenge) return;
    saveLastActiveProblem(problemDetails);
  }, [problemDetails?._id, contestId, challenge]);

  // Fetch the discussion count up front so the Discussion tab badge shows
  // even before the (heavier) Discussion tab itself has ever been opened.
  useEffect(() => {
    const targetSlug = problemDetails?.slug || slug;
    if (!targetSlug || contestId) return;
    let cancelled = false;
    proxy
      .get(`/problems/${targetSlug}/discussions`, { params: { limit: 1 } })
      .then(({ data }) => {
        if (!cancelled) setDiscussionCount(data?.pagination?.total || 0);
      })
      .catch(() => {
        /* non-critical — the Discussion tab will populate this once opened */
      });
    return () => {
      cancelled = true;
    };
  }, [problemDetails?.slug, slug, contestId]);

  const handleRunCode = async () => {
    if (!isLoggedIn) {
      const runsUsed = readGuestRunCount();
      if (runsUsed >= GUEST_FREE_RUNS) {
        dispatch(showAuth());
        return;
      }
      localStorage.setItem(GUEST_RUN_COUNT_KEY, String(runsUsed + 1));
    }
    setOutput("");
    setRunResults(null);
    setError(null);
    revealTestResults();
    setIsCompiling(true);
    try {
      const jobs = buildAllRunTestCases({
        problem: problemDetails,
        sampleCases: sampleTestCases,
        customCases,
        functionCaseOverrides,
        selectedSampleIndex,
        sampleInputOverride: useCustomInput ? "" : customInput,
      });

      if (jobs.length === 0) {
        toast.error(
          "No test cases to run. Add a sample or custom case with values filled in.",
        );
        setIsCompiling(false);
        return;
      }

      const useExecution = shouldUseExecutionEngine(
        problemDetails,
        contestJudgingMode,
      );

      const runOne = async (job) => {
        try {
          let data;
          if (useExecution) {
            data = await executionRun({
              sourceCode: code,
              language,
              languageId: languageMapping[language],
              slug: problemDetails.slug,
              testCase: job.testCase,
              contestId: contestId || null,
            });
          } else {
            const res = await proxy.post(
              "/code/compile",
              {
                source_code: code,
                language_id: languageMapping[language],
                testCase: job.testCase,
              },
              { headers: { "Content-Type": "application/json" } },
            );
            data = res.data;
          }
          return {
            key: job.key,
            label: job.label,
            status: data.status || "Error",
            message: data.message || "",
            output: data.output,
            expectedOutput: data.expectedOutput,
          };
        } catch (err) {
          return {
            key: job.key,
            label: job.label,
            status: "Error",
            message:
              err.response?.data?.message ||
              err.message ||
              "Error occurred while running the code.",
          };
        }
      };

      const results = await Promise.all(jobs.map(runOne));
      setRunResults(results);
      const anyFailed = results.some((r) => r.status !== "Accepted");
      setError(anyFailed);
      const summary = results.map((r) => `${r.label}: ${r.status}`).join("\n");
      setOutput(summary);
    } catch (error) {
      console.error("Error:", error);
      const msg =
        error.response?.data?.message ||
        "Error occurred while running the code.";
      setError(true);
      setRunResults(null);
      setOutput(msg);
    } finally {
      setIsCompiling(false);
    }
  };
  handleRunCodeRef.current = handleRunCode;

  const handleSubmitCode = async () => {
    if (!isLoggedIn) {
      dispatch(showAuth());
      return;
    }
    setOutput("");
    setRunResults(null);
    setError(null);
    revealTestResults();
    setIsSubmitting(true);

    try {
      const useExecution = shouldUseExecutionEngine(
        problemDetails,
        contestJudgingMode,
      );

      let data;
      if (useExecution) {
        data = await executionSubmit({
          sourceCode: code,
          language,
          languageId: languageMapping[language],
          slug: problemDetails.slug,
          contestId: contestId || null,
          challenge,
        });
      } else {
        const requestBody = {
          source_code: code,
          language_id: languageMapping[language],
          slug: problemDetails.slug,
          contestId,
          challenge,
        };
        const res = await proxy.post("/code/submit", requestBody, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        });
        data = res.data;
      }

      setOutput(data.message);
      setRunResults(null);
      setError(data.status !== "Accepted");

      if (!contestId && !challenge && problemDetails?.slug) {
        patchProblemAttemptedInCache(problemDetails.slug);
        setProblemDetails((prev) =>
          prev ? { ...prev, isAttempted: true } : prev,
        );
      }

      if (data.status === "Accepted") {
        setTimerAcceptedSignal((n) => n + 1);
        if (!contestId && !challenge && problemDetails?.slug) {
          patchProblemSolvedInCache(problemDetails.slug);
          setProblemDetails((prev) =>
            prev ? { ...prev, isSolved: true } : prev,
          );
        }
      }

      if (challenge === "75Hard") {
        callBack(data);
      }
      if (data.status === "Error" && data.message) {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Submission failed";
      toast.error(msg);
      setError(true);
      setOutput(msg);
    } finally {
      setIsSubmitting(false);
      setCurrentTab(submissionTabIndex);
      await refreshSubmissions();
    }
  };
  handleSubmitCodeRef.current = handleSubmitCode;

  const handleCodeChange = (newCode) => {
    const value = newCode || defaultCode[language];
    setCode(value);
    setSolutions((prev) => {
      const next = prev.map((s) =>
        s.id === activeSolutionId ? { ...s, code: value } : s,
      );
      persistSolutions(next, language, problemDetails);
      return next;
    });
  };

  const handleResetCode = () => {
    handleCodeChange(getStarterCode(problemDetails, language));
  };

  const handleSelectSolution = (id) => {
    if (id === activeSolutionId) return;
    const target = solutions.find((s) => s.id === id);
    if (!target) return;
    setActiveSolutionId(id);
    setCode(target.code);
    persistActiveSolutionId(id, language, problemDetails);
  };

  const handleAddSolution = () => {
    const newSolution = {
      id: `sol-${Date.now()}`,
      label: `Solution ${solutions.length + 1}`,
      code: getStarterCode(problemDetails, language),
    };
    const next = [...solutions, newSolution];
    setSolutions(next);
    setActiveSolutionId(newSolution.id);
    setCode(newSolution.code);
    persistSolutions(next, language, problemDetails);
    persistActiveSolutionId(newSolution.id, language, problemDetails);
  };

  const requestDeleteSolution = (id) => {
    if (solutions.length <= 1) {
      toast.info("You need at least one solution.");
      return;
    }
    setDeleteSolutionId(id);
  };

  const cancelDeleteSolution = () => setDeleteSolutionId(null);

  const confirmDeleteSolution = () => {
    const id = deleteSolutionId;
    setDeleteSolutionId(null);
    if (!id) return;
    const next = solutions
      .filter((s) => s.id !== id)
      .map((s, i) => ({ ...s, label: `Solution ${i + 1}` }));
    if (!next.length) return;
    setSolutions(next);
    persistSolutions(next, language, problemDetails);
    if (id === activeSolutionId) {
      const nextActive = next[0];
      setActiveSolutionId(nextActive.id);
      setCode(nextActive.code);
      persistActiveSolutionId(nextActive.id, language, problemDetails);
    }
  };

  const handleSettingsOpen = () => setOpenSettings(true);
  const handleSettingsClose = () => setOpenSettings(false);
  const handleThemeChange = (event) => setTheme(event.target.value);
  const handleFontSizeChange = (event) => {
    setFontSize(event.target.value);
    localStorage.setItem("fontSize", event.target.value);
  };
  const toggleFullscreen = async () => {
    try {
      const el = workspaceRef.current;
      const active =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (active) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } else if (el?.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el?.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen error:", err);
      toast.error("Fullscreen is not available or was blocked.");
    }
  };
  toggleFullscreenRef.current = toggleFullscreen;

  const handleCloseTab = () => {
    if (!consoleCollapsed) {
      setConsoleCollapsed(true);
      return;
    }
    if (currentTab !== 0) setCurrentTab(0);
  };
  closeTabRef.current = handleCloseTab;

  const toggleConsoleCollapsed = () => {
    setConsoleCollapsed((prev) => !prev);
  };

  const toggleFocusMode = () => {
    setFocusMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(FOCUS_MODE_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const togglePanelMaximize = () => {
    setIsPanelMaximized((prev) => !prev);
  };
  toggleMaximizeRef.current = togglePanelMaximize;

  const sampleTestCases = useMemo(
    () =>
      (problemDetails?.testCases || []).filter((tc) => tc.isSample !== false),
    [problemDetails?.testCases],
  );

  useEffect(() => {
    setSelectedSampleIndex(0);
    setUseCustomInput(false);
    setSelectedCustomIndex(0);
    setFunctionCaseOverrides({});
    setCustomCases([]);
    setRunResults(null);
    const sample = sampleTestCases[0];
    if (isFunctionMode(problemDetails)) {
      setCustomInput("");
    } else {
      setCustomInput(sample?.input ?? "");
    }
  }, [problemDetails?._id, problemDetails?.mode, sampleTestCases]);

  useEffect(() => {
    const syncFullscreen = () => {
      const active =
        document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(active === workspaceRef.current);
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("webkitfullscreenchange", syncFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("webkitfullscreenchange", syncFullscreen);
    };
  }, []);
  useEffect(() => {
    if (!isMobileLayout) return;
    setConsoleHeight((h) => Math.max(h, mobileConsoleMinHeight()));
  }, [isMobileLayout]);

  useEffect(() => {
    localStorage.setItem("problem-left-split", String(leftPanelPct));
  }, [leftPanelPct]);

  useEffect(() => {
    localStorage.setItem("problem-console-height", String(consoleHeight));
  }, [consoleHeight]);

  useEffect(() => {
    localStorage.setItem("problem-console-collapsed", String(consoleCollapsed));
  }, [consoleCollapsed]);

  useEffect(() => {
    if (!isMobileLayout || mobilePane !== "code") return undefined;
    const timers = [50, 200, 450].map((delay) =>
      window.setTimeout(() => {
        const node = editorAreaRef.current;
        if (!node) return;
        const next = Math.floor(node.getBoundingClientRect().height);
        if (next > 0) setEditorHeight(next);
        window.dispatchEvent(new Event("resize"));
      }, delay),
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [isMobileLayout, mobilePane, isPanelMaximized]);

  useEffect(() => {
    const node = editorAreaRef.current;
    if (!node) return undefined;

    const updateHeight = () => {
      const next = Math.floor(node.getBoundingClientRect().height);
      if (next > 0) setEditorHeight(next);
    };

    updateHeight();
    const timers = [100, 400].map((delay) =>
      window.setTimeout(updateHeight, delay),
    );
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    window.addEventListener("resize", updateHeight);

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [
    problemDetails?._id,
    isFullscreen,
    consoleCollapsed,
    isPanelMaximized,
    focusMode,
    leftPanelPct,
    currentTab,
  ]);

  const startHorizontalResize = (startX) => {
    const startPct = leftPanelPct;
    const container = splitContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const maxPctFromEditor =
      ((rect.width - EDITOR_PANEL_MIN_WIDTH - SPLIT_HANDLE_WIDTH) /
        rect.width) *
      100;
    const maxPct = Math.min(PANEL_SPLIT_MAX, maxPctFromEditor);

    const onMove = (e) => {
      const delta = e.clientX - startX;
      setLeftPanelPct(
        clamp(startPct + (delta / rect.width) * 100, PANEL_SPLIT_MIN, maxPct),
      );
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const startStackResize = (_startX, startY) => {
    const startPct = leftPanelPct;
    const container = splitContainerRef.current;
    if (!container) return;

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const delta = e.clientY - startY;
      setLeftPanelPct(
        clamp(
          startPct + (delta / rect.height) * 100,
          PANEL_SPLIT_MIN,
          PANEL_SPLIT_MAX,
        ),
      );
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const startConsoleResize = (_startX, startY) => {
    const startH = consoleHeight;

    const onMove = (e) => {
      setConsoleHeight(clamp(startH - (e.clientY - startY), 100, 560));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleTabChange = (event, newValue) => {
    if (!contestId && newValue === 1 && !isLoggedIn) {
      dispatch(showAuth());
      return;
    }
    if (newValue === submissionTabIndex && !isLoggedIn) {
      dispatch(showAuth());
      return;
    }
    setCurrentTab(newValue);
  };
  if (!problemDetails) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  const handleConfirmPublish = async (problemId) => {
    try {
      await proxy.put(`/problems/publish/${problemId}`);
      toast.success("Published successfully");
      navigate("/admin/manage-problems");
    } catch (error) {
      toast.error("Error publishing problem");
      console.error(error);
    }
  };

  const isDark = appTheme.palette.mode === "dark";
  const editorBorder = isDark ? "#30363d" : "rgba(15, 60, 70, 0.12)";
  const editorMuted = isDark ? "#8b949e" : "#5f6b73";
  const editorSurface = isDark ? "#161b22" : "#ffffff";
  const ACCENT = "#137274";
  const ACCENT_HOVER = "#0f5c5e";
  const isFocusLayout =
    (focusMode || isMobileLayout) && !contestId && !challenge && !isFullscreen;
  const useMobilePanes =
    isMobileLayout && !isFullscreen && !contestId && !challenge;
  const showProblemPane =
    !isPanelMaximized && (!useMobilePanes || mobilePane === "problem");
  const showEditorPane =
    !useMobilePanes || mobilePane === "code" || isPanelMaximized;

  const actionBtnSx = {
    textTransform: "none",
    fontSize: "0.8rem",
    fontWeight: 700,
    lineHeight: 1.2,
    px: 1.15,
    py: 0.35,
    minHeight: 30,
    flexShrink: 0,
    "& .MuiButton-startIcon": { mr: 0.5 },
    "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 16 },
  };

  const problemConsolePanel = (
    <IdeConsolePanel
      dataTour="problem-console"
      customInput={customInput}
      onCustomInputChange={setCustomInput}
      output={output}
      runResults={runResults}
      error={error}
      isCompiling={isCompiling}
      isSubmitting={isSubmitting}
      collapsed={consoleCollapsed}
      onToggleCollapse={toggleConsoleCollapsed}
      isFullscreen={isFullscreen}
      height={consoleHeight}
      minHeight={isMobileLayout ? mobileConsoleMinHeight() : 100}
      compact={isMobileLayout}
      border={editorBorder}
      muted={editorMuted}
      surface={editorSurface}
      isDark={isDark}
      sampleCases={sampleTestCases}
      problem={problemDetails}
      selectedCaseIndex={selectedSampleIndex}
      useCustomInput={useCustomInput}
      onUseCustomInputChange={setUseCustomInput}
      customCases={customCases}
      selectedCustomIndex={selectedCustomIndex}
      onSelectCustomCase={setSelectedCustomIndex}
      onAddCustomCase={() => {
        const next = {
          id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          input: "",
          args: {},
        };
        setCustomCases((prev) => {
          const list = [...prev, next];
          setSelectedCustomIndex(list.length - 1);
          return list;
        });
        setUseCustomInput(true);
      }}
      onRemoveCustomCase={(index) => {
        setCustomCases((prev) => {
          if (index < 0 || index >= prev.length) return prev;
          const list = prev.filter((_, i) => i !== index);
          if (list.length === 0) {
            setUseCustomInput(false);
            setSelectedCustomIndex(0);
            return list;
          }
          setSelectedCustomIndex((cur) => {
            if (cur === index) return Math.max(0, index - 1);
            if (cur > index) return cur - 1;
            return cur;
          });
          return list;
        });
      }}
      onCustomCaseChange={(index, updater) => {
        setCustomCases((prev) => {
          if (index < 0 || index >= prev.length) return prev;
          const current = prev[index] || { input: "", args: {} };
          const next =
            typeof updater === "function"
              ? updater(current)
              : { ...current, ...updater };
          const list = [...prev];
          list[index] = {
            id: current.id,
            input: next.input ?? "",
            args: next.args || {},
          };
          return list;
        });
      }}
      onSelectCase={(index) => {
        setUseCustomInput(false);
        setSelectedSampleIndex(index);
        const sample = sampleTestCases[index];
        if (!isFunctionMode(problemDetails) && sample?.input != null) {
          setCustomInput(sample.input);
        }
      }}
      functionMode={isFunctionMode(problemDetails)}
      caseOverride={functionCaseOverrides[selectedSampleIndex]}
      onCaseOverrideChange={(index, updater) => {
        setFunctionCaseOverrides((prev) => {
          const sample = sampleTestCases[index];
          const current = getEffectiveCaseData(sample, prev[index]);
          const next =
            typeof updater === "function" ? updater(current) : updater;
          return {
            ...prev,
            [index]: {
              args: next.args,
              expected: next.expected,
            },
          };
        });
      }}
      inputPlaceholder={
        isFunctionMode(problemDetails)
          ? functionModeInputPlaceholder
          : "Enter custom stdin…"
      }
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // Auto height is only needed for the mobile stacked layout (problem +
        // editor stacked in one column — see the "can't scroll under 100dvh
        // lock" note above). Desktop's side-by-side split still needs a
        // definite height so workspaceRef/splitContainerRef's flex:1/minHeight:0
        // chain resolves — otherwise the editor and console panes collapse.
        flex: (contestId || challenge) && isMobileLayout ? undefined : 1,
        height: (contestId || challenge) && isMobileLayout ? "auto" : "100%",
        minHeight: (contestId || challenge) && isMobileLayout ? "auto" : 0,
        px: isFocusLayout
          ? 0
          : { xs: useMobilePanes ? 1 : 1.5, sm: 2, md: 2.5 },
        py: isFocusLayout ? 0 : { xs: useMobilePanes ? 1 : 1.5, sm: 2 },
        background: isFocusLayout
          ? isDark
            ? "#0d1117"
            : "#fff"
          : appTheme.palette.mode === "dark"
            ? "transparent"
            : challenge || contestId
              ? "transparent"
              : "linear-gradient(180deg, #eef6f8 0%, #f7f9fb 50%, #ffffff 100%)",
      }}
    >
      {!isFullscreen && !isFocusLayout && !challenge && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: { xs: 1, md: 2 },
            mb: 1.75,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <ProblemBreadcrumb
              problemDetails={problemDetails}
              contestId={contestId}
            />
          </Box>
          <ProblemNeighborNav
            neighbors={problemDetails?.neighbors}
            contestId={contestId}
            challenge={challenge}
          />
        </Box>
      )}
      {loggedInUser?.role === "admin" &&
        !problemDetails.isPublished &&
        !isFullscreen && (
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor:
                appTheme.palette.mode === "dark"
                  ? "#30363d"
                  : "rgba(15,60,70,0.12)",
              bgcolor: appTheme.palette.mode === "dark" ? "#161b22" : "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: "0.85rem" }}
            >
              {problemDetails?.displayOrder}# Level -{problemDetails.level} -{" "}
              {problemDetails.section} - ({problemDetails.topics})
            </Typography>
            <Button
              onClick={() => handleConfirmPublish(problemDetails._id)}
              size="small"
              color="success"
              variant="contained"
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Publish
            </Button>
          </Box>
        )}
      <Helmet>
        <title>{formatProblemTitle(problemDetails.title)} | CrafterHack</title>
        <meta
          name="description"
          content={
            stripHtml(problemDetails.description) ||
            `Solve ${formatProblemTitle(problemDetails.title)} on CrafterHack.`
          }
        />
        <meta
          property="og:title"
          content={`${formatProblemTitle(problemDetails.title)} | CrafterHack`}
        />
        <meta
          property="og:description"
          content={
            stripHtml(problemDetails.description) ||
            `Solve ${formatProblemTitle(problemDetails.title)} on CrafterHack.`
          }
        />
        <meta
          property="og:url"
          content={`${window.location.origin}/problems/${problemDetails.slug}`}
        />
      </Helmet>

      {useMobilePanes && (
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            flexShrink: 0,
            mb: isFocusLayout ? 0 : 1,
            px: isFocusLayout ? 1 : 0,
            pt: isFocusLayout ? 1 : 0,
          }}
        >
          <Button
            fullWidth
            size="small"
            variant={mobilePane === "problem" ? "contained" : "outlined"}
            onClick={() => setMobilePane("problem")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              ...(mobilePane === "problem"
                ? { bgcolor: ACCENT, "&:hover": { bgcolor: ACCENT_HOVER } }
                : { borderColor: editorBorder, color: "text.primary" }),
            }}
          >
            Problem
          </Button>
          <Button
            fullWidth
            size="small"
            variant={mobilePane === "code" ? "contained" : "outlined"}
            onClick={() => setMobilePane("code")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              ...(mobilePane === "code"
                ? { bgcolor: ACCENT, "&:hover": { bgcolor: ACCENT_HOVER } }
                : { borderColor: editorBorder, color: "text.primary" }),
            }}
          >
            Code
          </Button>
        </Box>
      )}

      <Box
        ref={workspaceRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          "&:fullscreen": {
            bgcolor: isDark ? "#0d1117" : "#f4f8f9",
            p: { xs: 1, md: 1.5 },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
          },
          "&:-webkit-full-screen": {
            bgcolor: isDark ? "#0d1117" : "#f4f8f9",
            p: { xs: 1, md: 1.5 },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
          },
        }}
      >
        <Box
          ref={splitContainerRef}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            flex: 1,
            minHeight: 0,
            height: "100%",
            width: "100%",
            gap: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: showProblemPane ? "flex" : "none",
              flexDirection: "column",
              minHeight: 0,
              // Mobile panes fill height; desktop uses fixed % width so the drag handle works
              flex: {
                xs: useMobilePanes
                  ? 1
                  : isFullscreen
                    ? `0 0 ${leftPanelPct}%`
                    : 1,
                lg: "0 0 auto",
              },
              flexShrink: { xs: 1, lg: 0 },
              flexGrow: { lg: 0 },
              width: {
                xs: "100%",
                lg: `min(${leftPanelPct}%, calc(100% - ${EDITOR_PANEL_MIN_WIDTH + SPLIT_HANDLE_WIDTH}px))`,
              },
              height: {
                xs:
                  isFullscreen && !useMobilePanes ? `${leftPanelPct}%` : "100%",
                lg: "100%",
              },
              maxWidth: {
                lg: `calc(100% - ${EDITOR_PANEL_MIN_WIDTH + SPLIT_HANDLE_WIDTH}px)`,
              },
            }}
          >
            <Paper
              elevation={0}
              data-tour="problem-statement"
              sx={{
                p: 0,
                flex: 1,
                height: "100%",
                minHeight: 0,
                borderRadius: isFocusLayout ? 0 : 2.5,
                border: isFocusLayout ? "none" : "1px solid",
                borderRight: isFocusLayout
                  ? `1px solid ${editorBorder}`
                  : undefined,
                borderColor:
                  appTheme.palette.mode === "dark"
                    ? "#30363d"
                    : "rgba(15,60,70,0.1)",
                bgcolor: appTheme.palette.mode === "dark" ? "#161b22" : "#fff",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  borderBottom: "1px solid",
                  borderColor:
                    appTheme.palette.mode === "dark"
                      ? "#30363d"
                      : "rgba(15,60,70,0.1)",
                }}
              >
                <Tabs
                  value={currentTab}
                  onChange={handleTabChange}
                  variant={isFocusLayout ? "scrollable" : "fullWidth"}
                  scrollButtons={isFocusLayout ? "auto" : false}
                  sx={{
                    flex: 1,
                    minHeight: isFocusLayout ? 36 : 44,
                    minWidth: 0,
                    borderBottom: "none",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: isFocusLayout ? "0.8rem" : "0.85rem",
                      minHeight: isFocusLayout ? 36 : 44,
                      px: isFocusLayout ? 1.75 : undefined,
                    },
                    "& .Mui-selected": { color: "#137274" },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#137274",
                      height: 3,
                    },
                  }}
                >
                  <Tab label="Description" />
                  {!contestId && <Tab label="Editorial" />}
                  <Tab label="Submission" />
                  {!contestId && (
                    <Tab
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.6,
                          }}
                        >
                          Discussion
                          {discussionCount > 0 && (
                            <Box
                              component="span"
                              sx={{
                                minWidth: 18,
                                height: 16,
                                px: 0.5,
                                borderRadius: "8px",
                                bgcolor: isDark
                                  ? "rgba(19,114,116,0.25)"
                                  : "rgba(19,114,116,0.12)",
                                color: "#137274",
                                fontSize: "0.65rem",
                                fontWeight: 800,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                lineHeight: 1,
                              }}
                            >
                              {discussionCount}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  )}
                  {notesTabIndex !== -1 && (
                    <Tab
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          My Notes
                          {Boolean(problemDetails?.note) && (
                            <Box
                              component="span"
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                bgcolor: "#137274",
                              }}
                            />
                          )}
                        </Box>
                      }
                    />
                  )}
                </Tabs>
                {isFocusLayout && (
                  <ProblemNeighborNavInline
                    neighbors={problemDetails?.neighbors}
                  />
                )}
              </Box>
              {currentTab === discussionTabIndex && !contestId ? (
                <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                  <ProblemDiscussionTab
                    slug={problemDetails.slug || slug}
                    isLoggedIn={isLoggedIn}
                    active
                    enabled
                    initialPostId={discussionPostId}
                    onCountChange={setDiscussionCount}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                    overscrollBehavior: "contain",
                    touchAction: "pan-y",
                  }}
                >
                  {currentTab === 0 && (
                    <Box
                      sx={{
                        p: isFocusLayout
                          ? { xs: 1.5, sm: 2 }
                          : { xs: 1.75, sm: 2.25 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: isFocusLayout ? "center" : "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: isFocusLayout ? 1 : 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          component="h1"
                          sx={{
                            fontWeight: 750,
                            letterSpacing: "-0.03em",
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                            lineHeight: 1.25,
                            pr: 1,
                            color: "text.primary",
                          }}
                        >
                          {formatProblemTitle(problemDetails.title)}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            flexShrink: 0,
                          }}
                        >
                          {!contestId && (
                            <ProblemShareButton
                              slug={problemDetails.slug || slug}
                              title={formatProblemTitle(problemDetails.title)}
                            />
                          )}
                          {loggedInUser?.role === "admin" && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                navigate(
                                  `/admin/edit-problem/${problemDetails.slug}`,
                                )
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          mb: isFocusLayout ? 1.25 : 1.75,
                          flexWrap: "wrap",
                        }}
                      >
                        <DifficultyBar difficulty={problemDetails.difficulty} />
                        <ExecutionLimitsChip problem={problemDetails} />
                        <SolvedStatus
                          isSolved={problemDetails.isSolved}
                          isAttempted={problemDetails.isAttempted}
                        />
                      </Box>

                      <ProblemEngagementBar
                        slug={problemDetails.slug || slug}
                        initialLiked={problemDetails.isLiked}
                        initialLikeCount={problemDetails.likeCount}
                        initialBookmarked={problemDetails.isBookmarked}
                        contestId={contestId}
                        challenge={challenge}
                        onLikeChange={({ liked, likeCount }) => {
                          setProblemDetails((prev) =>
                            prev ? { ...prev, isLiked: liked, likeCount } : prev,
                          );
                        }}
                        onBookmarkChange={({ isBookmarked }) => {
                          setProblemDetails((prev) =>
                            prev ? { ...prev, isBookmarked } : prev,
                          );
                        }}
                      />

                      {!isFocusLayout && contestId === 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                            pb: 1.25,
                            borderBottom: "1px solid",
                            borderColor:
                              appTheme.palette.mode === "dark"
                                ? "#30363d"
                                : "rgba(15,60,70,0.1)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            Submissions:{" "}
                            {problemDetails.successSubmission +
                              problemDetails.failedSubmission}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            Success rate:{" "}
                            {problemDetails.successSubmission === 0 &&
                            problemDetails.failedSubmission === 0
                              ? "N/A"
                              : `${(
                                  (problemDetails.successSubmission /
                                    (problemDetails.successSubmission +
                                      problemDetails.failedSubmission)) *
                                  100
                                ).toFixed(2)}%`}
                          </Typography>
                        </Box>
                      )}

                      <Typography
                        variant="body1"
                        sx={problemStatementSx(
                          appTheme.palette.mode === "dark",
                        )}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeProblemText(
                            problemDetails.description,
                          ),
                        }}
                      />

                      <Divider sx={{ my: 2.5 }} />

                      <Typography
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          fontSize: "0.9rem",
                          letterSpacing: "0.02em",
                        }}
                      >
                        Constraints
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2,
                          color: "text.secondary",
                          lineHeight: 1.5,
                          bgcolor:
                            appTheme.palette.mode === "dark"
                              ? "#0d1117"
                              : "#f6f8fa",
                          border: "1px solid",
                          borderColor:
                            appTheme.palette.mode === "dark"
                              ? "#30363d"
                              : "rgba(15,60,70,0.08)",
                          borderRadius: 1.5,
                          p: 1.5,
                        }}
                        dangerouslySetInnerHTML={{
                          __html: formatProblemHtml(problemDetails.constraints),
                        }}
                      />

                      <Divider sx={{ my: 2.5 }} />

                      <Typography
                        sx={{
                          fontWeight: 700,
                          mb: 1.5,
                          fontSize: "0.9rem",
                          letterSpacing: "0.02em",
                        }}
                      >
                        Sample test cases
                      </Typography>
                      {(sampleTestCases || []).map((sample, index) => {
                        const display = getSampleDisplay(
                          sample,
                          problemDetails,
                        );
                        return (
                          <Box
                            key={index}
                            sx={{
                              mb: 1.75,
                              p: 1.5,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor:
                                appTheme.palette.mode === "dark"
                                  ? "#30363d"
                                  : "rgba(15,60,70,0.1)",
                              bgcolor:
                                appTheme.palette.mode === "dark"
                                  ? "#0d1117"
                                  : "#fafbfc",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                mb: 0.5,
                                fontSize: "0.8rem",
                              }}
                            >
                              {isFunctionMode(problemDetails)
                                ? `Example ${index + 1}`
                                : `Input ${index + 1}`}
                              {!isFunctionMode(problemDetails) && (
                                <Tooltip
                                  title={
                                    copiedText === display.input
                                      ? "Copied!"
                                      : "Copy Input"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopy(display.input)}
                                    sx={{ ml: 0.5 }}
                                  >
                                    <ContentCopyIcon
                                      sx={{ fontSize: "13px" }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Typography>
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                mb: 1.25,
                                p: 1.25,
                                borderRadius: 1.25,
                                bgcolor:
                                  appTheme.palette.mode === "dark"
                                    ? "#010409"
                                    : "#fff",
                                border: "1px solid",
                                borderColor:
                                  appTheme.palette.mode === "dark"
                                    ? "#30363d"
                                    : "rgba(15,60,70,0.08)",
                                fontSize: "0.8rem",
                                overflow: "auto",
                                fontFamily:
                                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                              }}
                            >
                              {isFunctionMode(problemDetails) ? (
                                <span>Input: {display.input}</span>
                              ) : (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: formatProblemHtml(display.input),
                                  }}
                                />
                              )}
                            </Box>

                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                mb: 0.5,
                                fontSize: "0.8rem",
                              }}
                            >
                              {isFunctionMode(problemDetails)
                                ? `Output ${index + 1}`
                                : `Expected output ${index + 1}`}
                              {!isFunctionMode(problemDetails) && (
                                <Tooltip
                                  title={
                                    copiedText === display.output
                                      ? "Copied!"
                                      : "Copy Output"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopy(display.output)}
                                    sx={{ ml: 0.5 }}
                                  >
                                    <ContentCopyIcon
                                      sx={{ fontSize: "13px" }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Typography>
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                mb: sample.explanation ? 1.25 : 0,
                                p: 1.25,
                                borderRadius: 1.25,
                                bgcolor:
                                  appTheme.palette.mode === "dark"
                                    ? "#010409"
                                    : "#fff",
                                border: "1px solid",
                                borderColor:
                                  appTheme.palette.mode === "dark"
                                    ? "#30363d"
                                    : "rgba(15,60,70,0.08)",
                                fontSize: "0.8rem",
                                overflow: "auto",
                                fontFamily:
                                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                              }}
                            >
                              {isFunctionMode(problemDetails) ? (
                                <span>{display.output}</span>
                              ) : (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: formatProblemHtml(display.output),
                                  }}
                                />
                              )}
                            </Box>

                            {sample.explanation && (
                              <>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    mb: 0.5,
                                    mt: 0.5,
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  Explanation {index + 1}
                                </Typography>
                                <Box
                                  component="pre"
                                  sx={{
                                    m: 0,
                                    p: 1.25,
                                    borderRadius: 1.25,
                                    bgcolor:
                                      appTheme.palette.mode === "dark"
                                        ? "#010409"
                                        : "#fff",
                                    border: "1px solid",
                                    borderColor:
                                      appTheme.palette.mode === "dark"
                                        ? "#30363d"
                                        : "rgba(15,60,70,0.08)",
                                    fontSize: "0.8rem",
                                    overflow: "auto",
                                    whiteSpace: "pre-wrap",
                                    fontFamily:
                                      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                  }}
                                >
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: formatProblemHtml(
                                        sample.explanation,
                                      ),
                                    }}
                                  />
                                </Box>
                              </>
                            )}
                          </Box>
                        );
                      })}

                      <ProblemHint problemDetails={problemDetails} />
                    </Box>
                  )}

                  {currentTab === 1 && !contestId && (
                    <ProblemExplanationTab
                      videoLink={problemDetails.videoLink}
                      testCases={problemDetails.testCases}
                    />
                  )}

                  {currentTab === submissionTabIndex && (
                    <ProblemSubmissionsTab
                      submissions={submissions}
                      loading={submissionsLoading}
                      error={submissionsError}
                      onRefresh={refreshSubmissions}
                    />
                  )}

                  {currentTab === notesTabIndex && notesTabIndex !== -1 && (
                    <ProblemNotesTab
                      slug={problemDetails.slug || slug}
                      isLoggedIn={isLoggedIn}
                      initialNote={problemDetails.note}
                      initialUpdatedAt={problemDetails.noteUpdatedAt}
                      onNoteChange={({ note, updatedAt }) => {
                        setProblemDetails((prev) =>
                          prev
                            ? { ...prev, note, noteUpdatedAt: updatedAt }
                            : prev,
                        );
                      }}
                    />
                  )}
                </Box>
              )}
            </Paper>
          </Box>

          {!isPanelMaximized && (
            <ResizeHandle
              direction="horizontal"
              onResizeStart={startHorizontalResize}
              sx={{
                display: useMobilePanes ? "none" : { xs: "none", lg: "block" },
              }}
            />
          )}
          {!isPanelMaximized && (
            <ResizeHandle
              direction="vertical"
              onResizeStart={startStackResize}
              sx={{
                display: { xs: isFullscreen ? "block" : "none", lg: "none" },
              }}
            />
          )}

          <Box
            sx={{
              display: showEditorPane ? "flex" : "none",
              flexDirection: "column",
              minHeight: 0,
              minWidth: { xs: "100%", lg: EDITOR_PANEL_MIN_WIDTH },
              // Fill remaining space after the %width problem pane (desktop stretch)
              flex: { xs: 1, lg: "1 1 0" },
              width: { xs: "100%", lg: "auto" },
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Paper
              elevation={0}
              data-tour="problem-editor"
              sx={{
                p: 0,
                position: "relative",
                borderRadius: isFocusLayout ? 0 : 2.5,
                border: isFocusLayout ? "none" : `1px solid ${editorBorder}`,
                bgcolor: editorSurface,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                height: "100%",
              }}
            >
              <Box
                data-tour="problem-actions"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 1,
                  borderBottom: `1px solid ${editorBorder}`,
                  flexWrap: "wrap",
                  flexShrink: 0,
                }}
              >
                <TextField
                  select
                  size="small"
                  aria-label="Language"
                  value={language}
                  onChange={(e) => {
                    setLanguage(e.target.value);
                    localStorage.setItem("lang", e.target.value);
                  }}
                  sx={{
                    minWidth: 100,
                    flexShrink: 0,
                    "& .MuiInputBase-root": {
                      fontSize: "0.8rem",
                      minHeight: 30,
                      height: 30,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiSelect-select": {
                      py: 0.35,
                      minHeight: "unset",
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                >
                  {functionLanguages.map((lang) => (
                    <MenuItem key={lang.id} value={lang.id}>
                      {lang.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Button
                  variant="contained"
                  size="small"
                  onClick={handleRunCode}
                  disabled={isCompiling || isSubmitting}
                  startIcon={
                    isCompiling ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <PlayArrowIcon />
                    )
                  }
                  title={
                    shortcutsOn
                      ? `Run (${PROBLEM_SHORTCUTS[0].keys().join("")})`
                      : "Run"
                  }
                  sx={{
                    ...actionBtnSx,
                    fontWeight: 800,
                    bgcolor: ACCENT,
                    "&:hover": { bgcolor: ACCENT_HOVER },
                  }}
                >
                  {isCompiling ? "Running…" : "Run"}
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSubmitCode}
                  disabled={isSubmitting || isCompiling}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <CheckCircleIcon />
                    )
                  }
                  title={
                    shortcutsOn
                      ? `Submit (${PROBLEM_SHORTCUTS[1].keys().join("")})`
                      : "Submit"
                  }
                  sx={{
                    ...actionBtnSx,
                    bgcolor: ACCENT_HOVER,
                    "&:hover": { bgcolor: "#0a4a4c" },
                  }}
                >
                  {isSubmitting ? "Submitting…" : "Submit"}
                </Button>

                <ProblemSolveTimer
                  problemSlug={problemDetails?.slug}
                  difficulty={problemDetails?.difficulty}
                  acceptedSignal={timerAcceptedSignal}
                />

                <Box sx={{ flex: 1 }} />

                {!contestId && (
                  <ProblemShareButton
                    slug={problemDetails.slug || slug}
                    title={formatProblemTitle(problemDetails.title)}
                  />
                )}
                {!contestId && !challenge && !isMobileLayout && (
                  <Tooltip
                    title={
                      focusMode ? "Show full layout" : "Focus mode — minimal UI"
                    }
                  >
                    <IconButton
                      data-tour="problem-focus-mode"
                      onClick={toggleFocusMode}
                      size="small"
                      sx={{
                        color: focusMode ? ACCENT : editorMuted,
                        flexShrink: 0,
                      }}
                    >
                      {focusMode ? (
                        <CenterFocusStrongIcon fontSize="small" />
                      ) : (
                        <CenterFocusWeakIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                {!contestId && !challenge && (
                  <Tooltip title="Take a quick tour">
                    <IconButton
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent("ch-start-problem-solve-tour"),
                        )
                      }
                      size="small"
                    >
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Reset Code">
                  <IconButton onClick={handleResetCode} size="small">
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Settings">
                  <IconButton onClick={handleSettingsOpen} size="small">
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    isPanelMaximized
                      ? `Exit maximize panel (${altKey()}+)`
                      : `Maximize panel (${altKey()}+)`
                  }
                >
                  <IconButton onClick={togglePanelMaximize} size="small">
                    {isPanelMaximized ? (
                      <CloseFullscreenOutlinedIcon fontSize="small" />
                    ) : (
                      <OpenInFullIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    isFullscreen
                      ? `Exit fullscreen (${altKey()}F)`
                      : `Fullscreen (${altKey()}F)`
                  }
                >
                  <IconButton onClick={toggleFullscreen} size="small">
                    {isFullscreen ? (
                      <CloseFullscreenIcon fontSize="small" />
                    ) : (
                      <FullscreenIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              <Box
                data-tour="problem-solutions"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.85,
                  borderBottom: `1px solid ${editorBorder}`,
                  flexShrink: 0,
                }}
              >
                {solutions.map((solution) => (
                  <Chip
                    key={solution.id}
                    label={solution.label}
                    size="small"
                    onClick={() => handleSelectSolution(solution.id)}
                    onDelete={
                      solutions.length > 1
                        ? () => requestDeleteSolution(solution.id)
                        : undefined
                    }
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      bgcolor:
                        solution.id === activeSolutionId
                          ? ACCENT
                          : isDark
                            ? "#21262d"
                            : "rgba(15,60,70,0.08)",
                      color:
                        solution.id === activeSolutionId ? "#fff" : editorMuted,
                      "& .MuiChip-deleteIcon": {
                        color:
                          solution.id === activeSolutionId
                            ? "rgba(255,255,255,0.7)"
                            : editorMuted,
                        "&:hover": {
                          color:
                            solution.id === activeSolutionId
                              ? "#fff"
                              : "#e5534b",
                        },
                      },
                    }}
                  />
                ))}
                <Tooltip title="Add a new solution">
                  <IconButton
                    onClick={handleAddSolution}
                    size="small"
                    sx={{ color: editorMuted }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {isFunctionMode(problemDetails) &&
                problemDetails?.stubHint?.trim() && (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderBottom: `1px solid ${editorBorder}`,
                      bgcolor: isDark
                        ? alpha(ACCENT, 0.08)
                        : alpha(ACCENT, 0.06),
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.78rem",
                        color: editorMuted,
                        lineHeight: 1.45,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: problemDetails.stubHint,
                      }}
                    />
                  </Box>
                )}

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Box
                  ref={editorAreaRef}
                  data-tour="problem-code"
                  sx={{ flex: 1, minHeight: 0, width: "100%" }}
                >
                  <Editor
                    height={editorHeight}
                    width="100%"
                    language={language === "cpp" ? "cpp" : language}
                    value={code}
                    theme={theme}
                    beforeMount={defineCrafterhackThemes}
                    onChange={handleCodeChange}
                    options={monacoOptions}
                    loading={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: editorHeight,
                          minHeight: 200,
                          color: editorMuted,
                          fontSize: "0.85rem",
                          fontWeight: 600,
                        }}
                      >
                        Loading editor…
                      </Box>
                    }
                  />
                </Box>

                {!consoleCollapsed && (
                  <ResizeHandle
                    direction="vertical"
                    onResizeStart={startConsoleResize}
                  />
                )}
                {problemConsolePanel}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Modal open={openSettings} onClose={handleSettingsClose}>
        <Box
          sx={{
            backgroundColor:
              appTheme.palette.mode === "dark" ? "#161b22" : "#fff",
            padding: "24px",
            borderRadius: "12px",
            boxShadow: 3,
            width: 440,
            maxWidth: "92%",
            margin: "auto",
            marginTop: "80px",
            maxHeight: "calc(100vh - 120px)",
            overflow: "auto",
            border: "1px solid",
            borderColor:
              appTheme.palette.mode === "dark"
                ? "#30363d"
                : "rgba(15,60,70,0.1)",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, mb: 2, letterSpacing: "-0.02em" }}
          >
            Editor Settings
          </Typography>
          <TextField
            select
            label="Theme"
            value={theme}
            onChange={handleThemeChange}
            sx={{ mb: 2, width: "100%" }}
          >
            <MenuItem value={CRAFTERHACK_DARK_THEME}>Dark</MenuItem>
            <MenuItem value={CRAFTERHACK_LIGHT_THEME}>Light</MenuItem>
          </TextField>
          <TextField
            select
            label="Font Size"
            value={fontSize}
            onChange={handleFontSizeChange}
            sx={{ mb: 2.5, width: "100%" }}
          >
            {[12, 13, 14, 16, 18, 20].map((size) => (
              <MenuItem key={size} value={size}>
                {size}px
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={autoSuggest}
                onChange={(e) => {
                  const on = e.target.checked;
                  setAutoSuggest(on);
                  try {
                    localStorage.setItem("autoSuggest", String(on));
                  } catch {
                    /* ignore */
                  }
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                  Auto suggestions
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: editorMuted }}>
                  Suggest words already used in your code as you type. Not full
                  IntelliSense — method/member completions aren't available for
                  most languages yet.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", mb: 2.5, ml: 0, width: "100%" }}
          />

          <Divider sx={{ mb: 2 }} />

          {!contestId && !challenge && !isMobileLayout && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={focusMode}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setFocusMode(on);
                    try {
                      localStorage.setItem(FOCUS_MODE_KEY, String(on));
                    } catch {
                      /* ignore */
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                    Focus mode
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: editorMuted }}>
                    Minimal layout — problem statement and editor only.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", mb: 2.5, ml: 0, width: "100%" }}
            />
          )}
          {isMobileLayout && !contestId && !challenge && (
            <Typography
              sx={{
                fontSize: "0.78rem",
                color: editorMuted,
                mb: 2,
                lineHeight: 1.45,
              }}
            >
              Focus layout is always on for phones and iPads.
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.25,
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
              Keyboard shortcuts
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={shortcutsOn}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setShortcutsOn(on);
                    setShortcutsEnabled(on);
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: editorMuted,
                  }}
                >
                  Enabled
                </Typography>
              }
              sx={{ mr: 0 }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ color: editorMuted, mb: 1.5, fontSize: "0.8rem" }}
          >
            Shortcuts work while solving — including inside the code editor.
          </Typography>

          <Box
            sx={{
              border: `1px solid ${editorBorder}`,
              borderRadius: 2,
              overflow: "hidden",
              opacity: shortcutsOn ? 1 : 0.55,
            }}
          >
            {PROBLEM_SHORTCUTS.map((row, i) => (
              <Box
                key={row.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                  px: 1.5,
                  py: 1.15,
                  borderTop: i === 0 ? "none" : `1px solid ${editorBorder}`,
                  bgcolor: isDark ? alpha("#fff", 0.02) : "#fafbfc",
                }}
              >
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  {row.action}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {row.keys().map((k, ki) => (
                    <React.Fragment key={`${row.id}-${ki}`}>
                      {ki > 0 && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.7rem",
                            color: editorMuted,
                            fontWeight: 700,
                          }}
                        >
                          +
                        </Typography>
                      )}
                      <Box
                        component="kbd"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 26,
                          height: 24,
                          px: 0.75,
                          borderRadius: 1,
                          border: `1px solid ${editorBorder}`,
                          bgcolor: isDark ? "#0d1117" : "#fff",
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          color: isDark ? "#e6edf3" : "#0f2a2b",
                          boxShadow: isDark
                            ? "none"
                            : "0 1px 0 rgba(15,60,70,0.08)",
                        }}
                      >
                        {k}
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Modal>

      <Modal open={Boolean(deleteSolutionId)} onClose={cancelDeleteSolution}>
        <Box
          sx={{
            backgroundColor:
              appTheme.palette.mode === "dark" ? "#161b22" : "#fff",
            padding: "24px",
            borderRadius: "12px",
            boxShadow: 3,
            width: 380,
            maxWidth: "92%",
            margin: "auto",
            marginTop: "160px",
            border: "1px solid",
            borderColor:
              appTheme.palette.mode === "dark"
                ? "#30363d"
                : "rgba(15,60,70,0.1)",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, mb: 1, letterSpacing: "-0.02em" }}
          >
            Delete this solution?
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: editorMuted, mb: 3, fontSize: "0.85rem" }}
          >
            {solutions.find((s) => s.id === deleteSolutionId)?.label} and its
            code will be permanently removed. This can't be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={cancelDeleteSolution}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteSolution}
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#e5534b",
                boxShadow: "none",
                "&:hover": { bgcolor: "#c8433c", boxShadow: "none" },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ProblemPage;

const ExecutionLimitsChip = ({ problem }) => {
  const label = formatExecutionLimitsLabel(resolveLimitsForDisplay(problem));
  if (!label) return null;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 700,
        fontSize: "0.72rem",
        height: 26,
        bgcolor: "action.hover",
      }}
    />
  );
};

// Function to return difficulty with a custom styled bar
const DifficultyBar = ({ difficulty }) => {
  const level = (difficulty || "easy").toLowerCase();
  const difficultyColors = {
    easy: "#1a7f37",
    medium: "#d97706",
    hard: "#cf222e",
  };
  const color = difficultyColors[level] || difficultyColors.easy;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        py: 0.35,
        borderRadius: 1.25,
        bgcolor: `${color}18`,
        border: `1px solid ${color}40`,
      }}
    >
      <Box
        sx={{
          backgroundColor: color,
          width: 8,
          height: 8,
          borderRadius: "50%",
        }}
      />
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color, fontSize: "0.75rem" }}
      >
        {level.charAt(0).toUpperCase() + level.slice(1)}
      </Typography>
    </Box>
  );
};

// Function to show custom solved/attempted status
export const SolvedStatus = ({ isSolved, isAttempted }) => {
  if (!isSolved && !isAttempted) return null;
  const color = isSolved ? "#1a7f37" : "#d97706";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 1,
        py: 0.35,
        borderRadius: 1.25,
        bgcolor: `rgba(${isSolved ? "26,127,55" : "217,119,6"},0.1)`,
        border: `1px solid rgba(${isSolved ? "26,127,55" : "217,119,6"},0.25)`,
      }}
    >
      {isSolved ? (
        <CheckCircleIcon sx={{ color, fontSize: 15 }} />
      ) : (
        <PendingOutlinedIcon sx={{ color, fontSize: 15 }} />
      )}
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color, fontSize: "0.75rem" }}
      >
        {isSolved ? "Solved" : "Attempted"}
      </Typography>
    </Box>
  );
};
